'use client';
import React from 'react';

export type SentimentType = 'Positive' | 'Satisfied' | 'Neutral' | 'Concerned' | 'Frustrated' | 'Angry' | 'Disappointed' | 'Escalation Risk';

interface SentimentConfig {
  icon: string;
  label: string;
  textCls: string;
  bgCls: string;
  borderCls: string;
}

export const sentimentConfig: Record<SentimentType, SentimentConfig> = {
  Positive: {
    icon: '😊',
    label: 'Positive',
    textCls: 'text-green-700',
    bgCls: 'bg-green-50',
    borderCls: 'border-green-200',
  },
  Satisfied: {
    icon: '🙂',
    label: 'Satisfied',
    textCls: 'text-emerald-700',
    bgCls: 'bg-emerald-50',
    borderCls: 'border-emerald-200',
  },
  Neutral: {
    icon: '😐',
    label: 'Neutral',
    textCls: 'text-slate-600',
    bgCls: 'bg-slate-50',
    borderCls: 'border-slate-200',
  },
  Concerned: {
    icon: '😕',
    label: 'Concerned',
    textCls: 'text-amber-700',
    bgCls: 'bg-amber-50',
    borderCls: 'border-amber-200',
  },
  Frustrated: {
    icon: '😣',
    label: 'Frustrated',
    textCls: 'text-orange-700',
    bgCls: 'bg-orange-50',
    borderCls: 'border-orange-200',
  },
  Angry: {
    icon: '😠',
    label: 'Angry',
    textCls: 'text-red-700',
    bgCls: 'bg-red-50',
    borderCls: 'border-red-200',
  },
  Disappointed: {
    icon: '😞',
    label: 'Disappointed',
    textCls: 'text-purple-700',
    bgCls: 'bg-purple-50',
    borderCls: 'border-purple-200',
  },
  'Escalation Risk': {
    icon: '⚠️',
    label: 'Escalation Risk',
    textCls: 'text-red-700',
    bgCls: 'bg-red-50',
    borderCls: 'border-red-300',
  },
};

interface SentimentBadgeProps {
  sentiment: SentimentType;
  showBg?: boolean;
  confidence?: number;
  size?: 'sm' | 'md';
}

export default function SentimentBadge({
  sentiment,
  showBg = false,
  confidence,
  size = 'sm',
}: SentimentBadgeProps) {
  const cfg = sentimentConfig[sentiment] ?? sentimentConfig['Neutral'];
  const textSize = size === 'md' ? 'text-[13px]' : 'text-[12px]';
  const iconSize = size === 'md' ? 'text-[15px]' : 'text-[13px]';

  if (showBg) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${textSize} font-600 px-2 py-0.5 rounded-md border ${cfg.textCls} ${cfg.bgCls} ${cfg.borderCls} sentiment-transition`}
      >
        <span className={`sentiment-icon ${iconSize}`}>{cfg.icon}</span>
        <span>{cfg.label}</span>
        {confidence !== undefined && (
          <span className="opacity-70 font-500">{confidence}%</span>
        )}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 ${textSize} font-500 ${cfg.textCls} sentiment-transition`}>
      <span className={`sentiment-icon ${iconSize}`}>{cfg.icon}</span>
      <span>{cfg.label}</span>
      {confidence !== undefined && (
        <span className="opacity-60 text-[11px]">{confidence}%</span>
      )}
    </span>
  );
}